import { useState } from "react";
import { Camera, Plus, Trash2, Wand2 } from "lucide-react";
import { api } from "../../lib/api";
import { buttonClass, fieldClass, primaryClass, WorkflowError, useWorkflowAction } from "./shared";
import { PlanEditor } from "./PlanEditor";

const TEMPLATE = [
  ["全景（敷地全体）", "敷地の角に立ち、敷地全体が入るように横向きで撮影。反対側の角からも1枚。", true],
  ["進入路・前面道路", "トラック・重機が入る道路を正面から。道幅が分かるように道路の端から端まで入れる。", true],
  ["境界・隣地", "境界杭・ブロック塀・フェンスなど、隣の土地との境目が分かる所を撮影。", true],
  ["作業対象のアップ", "木・草・構造物など作業する物に近づいて撮影。大きさが分かるよう人や物を横に並べる。", true],
  ["障害物・危険箇所", "電線・水路・急な傾斜・埋設物の表示など、注意が必要な所を撮影。", false],
  ["駐車・資材置き場", "車両の駐車や資材を置ける場所の広さが分かるように撮影。", false],
];

const newItem = (title = "", instruction = "", required = true) => ({ id: crypto.randomUUID().slice(0, 8), title, instruction, required, x: null, y: null, angle: -90 });

const ShotPhotos = ({ photos }) => photos.length ? (
  <div className="flex flex-wrap gap-2">
    {photos.map((ph) => (
      <a key={ph.id} data-testid={`guide-photo-${ph.id}`} href={`${api.defaults.baseURL}/site-photos/${ph.id}/image`} target="_blank" rel="noreferrer" className="relative block">
        <img src={`${api.defaults.baseURL}/site-photos/${ph.id}/image`} alt="" className="h-16 w-16 border border-line object-cover" />
        <span className={`absolute bottom-0 left-0 right-0 bg-ink/80 text-center text-[9px] ${ph.status === "差戻し" ? "text-red-300" : ph.status === "承認" ? "text-green-300" : "text-amber-300"}`}>{ph.status}</span>
      </a>
    ))}
  </div>
) : <p className="text-xs text-gray-600">未撮影</p>;

export const PhotoGuidePanel = ({ data, onChanged }) => {
  const { project: p, workflow: w, survey_photos: photos = [] } = data;
  const [items, setItems] = useState(w.photo_guide || []);
  const [plan, setPlan] = useState(w.photo_plan || { kind: "none", template: "", image_id: "" });
  const [selected, setSelected] = useState(null);
  const { busy, error, run } = useWorkflowAction(p.id, onChanged);
  const update = (i, key, value) => setItems((list) => list.map((it, j) => (j === i ? { ...it, [key]: value } : it)));
  return (
    <section data-testid="workflow-photo-guide" className="space-y-4 border-t border-line pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-bold text-white">現地写真の撮影指示（スタッフに表示）</h2>
          <p className="mt-1 text-xs text-gray-500">平面図に撮る位置と向きを置くと、スタッフは現地で番号を押してそのまま撮影できます。</p>
        </div>
        <button type="button" data-testid="photo-guide-template" onClick={() => setItems((list) => [...list, ...TEMPLATE.map((t) => newItem(...t))])} className={buttonClass}><Wand2 className="h-4 w-4" />標準の指示を追加</button>
      </div>
      <PlanEditor projectId={p.id} plan={plan} setPlan={setPlan} items={items} setItems={setItems} selected={selected} setSelected={setSelected} photos={photos} />
      {items.length === 0 && <p data-testid="photo-guide-empty" className="text-sm text-gray-500">撮影指示はまだありません。「標準の指示を追加」または「項目を追加」で作成してください。</p>}
      <ol className="space-y-3">
        {items.map((it, i) => (
          <li key={it.id} data-testid={`photo-guide-item-${i}`} className={`grid gap-3 border bg-panel p-3 lg:grid-cols-[2.5rem_1fr_auto] ${selected === i ? "border-accent" : "border-line"}`}>
            <button type="button" data-testid={`photo-guide-select-${i}`} onClick={() => setSelected(i)} title="平面図に配置する番号として選ぶ" className={`flex h-9 w-9 items-center justify-center rounded-full font-mono text-sm font-bold ${selected === i ? "bg-accent text-ink" : "border border-accent/60 text-accent"}`}>{i + 1}</button>
            <div className="min-w-0 space-y-2">
              <input data-testid={`photo-guide-title-${i}`} value={it.title} onChange={(e) => update(i, "title", e.target.value)} placeholder="撮る場所（例：全景）" className={fieldClass} />
              <textarea data-testid={`photo-guide-instruction-${i}`} rows={2} value={it.instruction} onChange={(e) => update(i, "instruction", e.target.value)} placeholder="撮り方（立つ位置・向き・入れる物など）" className={fieldClass} />
              <ShotPhotos photos={photos.filter((ph) => ph.guide_id === it.id)} />
            </div>
            <div className="flex items-start gap-3">
              <label className="flex items-center gap-2 text-xs text-gray-300"><input data-testid={`photo-guide-required-${i}`} type="checkbox" checked={it.required} onChange={(e) => update(i, "required", e.target.checked)} className="accent-accent" />必須</label>
              <button type="button" data-testid={`photo-guide-remove-${i}`} onClick={() => { setItems((list) => list.filter((_, j) => j !== i)); setSelected(null); }} className="p-1 text-gray-500 hover:text-red-400" aria-label="削除"><Trash2 className="h-4 w-4" /></button>
            </div>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-3">
        <button type="button" data-testid="photo-guide-add" onClick={() => { setItems((list) => [...list, newItem()]); setSelected(items.length); }} className={buttonClass}><Plus className="h-4 w-4" />項目を追加</button>
        <button type="button" data-testid="photo-guide-save" disabled={busy || items.some((it) => !it.title.trim())} onClick={() => run("put", "photo-guide", { plan, items: items.map((it) => ({ ...it, title: it.title.trim() })) })} className={primaryClass}><Camera className="h-4 w-4" />撮影指示を保存</button>
      </div>
      <WorkflowError error={error} id="photo-guide-error" />
    </section>
  );
};
